import axios from 'axios'

/**
 * Axios クライアント設定
 *
 * - バックエンドAPIへのリクエストに使用
 * - JWT トークンを自動的にヘッダーに追加
 * - エラーハンドリング
 * - エラーログ自動収集（frontend/logs/error.log に保存）
 */
const apiClient = axios.create({
  baseURL: import.meta.env.VITE_API_BASE_URL || 'http://localhost:8005',
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
})

// リクエストインターセプター: JWT トークンを自動追加
apiClient.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('auth_token')
    if (token) {
      config.headers.Authorization = `Bearer ${token}`
    }
    return config
  },
  (error) => {
    return Promise.reject(error)
  }
)

// レスポンスインターセプター: エラーハンドリング + ログ送信
apiClient.interceptors.response.use(
  (response) => {
    return response
  },
  (error) => {
    // エラーログ送信
    sendErrorLog({
      type: 'api_error',
      message: error.message,
      endpoint: error.config?.url,
      method: error.config?.method?.toUpperCase(),
      status: error.response?.status,
      statusText: error.response?.statusText,
      requestBody: error.config?.data ? JSON.parse(error.config.data) : null,
      responseBody: error.response?.data,
    })

    if (error.response?.status === 401) {
      // 認証エラー: ログイン画面にリダイレクト
      console.error('[API Client] 401 Unauthorized detected:', {
        url: error.config?.url,
        method: error.config?.method,
        headers: error.config?.headers,
        response: error.response?.data
      })
      localStorage.removeItem('auth_token')
      localStorage.removeItem('user_data')
      window.location.href = '/auth/login'
    }
    return Promise.reject(error)
  }
)

/**
 * エラーログ送信関数
 * backend API にエラー情報を送信
 * ログ送信失敗でもアプリケーションの動作には影響しない
 * 
 * 無限ループ防止:
 * - 専用のaxiosインスタンスを使用（インターセプターなし）
 * - ログ送信エンドポイントへのエラーは送信しない
 */
let isLoggingError = false // 無限ループ防止フラグ

function sendErrorLog(errorData) {
  // 無限ループ防止: ログ送信中またはログ送信エンドポイントへのエラーはスキップ
  if (isLoggingError || errorData.endpoint?.includes('/logs/frontend')) {
    return
  }

  isLoggingError = true

  // ユーザー情報取得
  let userId = null
  let hasAuthToken = false
  try {
    const userData = localStorage.getItem('user_data')
    if (userData) {
      userId = JSON.parse(userData).id
    }
    hasAuthToken = !!localStorage.getItem('auth_token')
  } catch (e) {}

  // 専用のaxiosインスタンス（インターセプターなし）
  const logClient = axios.create({
    baseURL: import.meta.env.VITE_API_BASE_URL || 'http://localhost:8005',
    timeout: 5000,
    headers: {
      'Content-Type': 'application/json',
    },
  })

  logClient.post('/console/v1/logs/frontend', {
    ...errorData,
    timestamp: new Date().toISOString(),
    url: window.location.href,
    userId: userId,
    hasAuthToken: hasAuthToken,
    browser: {
      userAgent: navigator.userAgent,
      language: navigator.language,
      platform: navigator.platform,
      screenWidth: window.screen.width,
      screenHeight: window.screen.height,
      windowWidth: window.innerWidth,
      windowHeight: window.innerHeight,
    }
  }).catch(() => {
    // ログ送信失敗は完全に無視
  }).finally(() => {
    isLoggingError = false
  })
}

export default apiClient
